import React, { useCallback, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { Color } from "../../../../../../../../../../components/Form/Button";
import Checkbox from "../../../../../../../../../../components/Form/Checkbox";
import Modal from "../../../../../../../../../../components/Generic/Modal";
import { IUpdateMapCells } from "../../../../../../../../../../hooks/useWarehouseMapActions";
import { WarehouseMap } from "../../../../../../../../../../models/warehouse.map";
import { MapCell } from "../../../../../../../../../../models/warehouse.mapcell";
import { MapStatus } from "../../../../../../../../../../util/enums";
import { PreviewStatus } from "../../../../../../../../Moving/Movement/components/Map/PreviewStatus";
import {
  BlankCheckbox,
  MapCellsLevelsContainer,
  MapCellsLevelsWrapper,
  MapInput,
  MapRow,
  MapSquare,
} from "./styles";

interface Props {
  open: boolean;
  closeModal: () => void;
  isOnViewMode?: boolean;
  hasError?: boolean;
  mapSelected?: WarehouseMap;
  mapCellSelected?: MapCell;
  isLoading?: boolean;
  onCreate?: (mapCells: MapCell[]) => void;
  onUpdate?: (mapCells: IUpdateMapCells[]) => void;
  onClear?: (id: number) => void;
  createdMapCells?: MapCell[];
  levelsSize?: number;
}

enum AxisNames {
  ROW_NAME = "rowName",
  COLUMN_NAME = "columnName",
  LEVEL_NAME = "levelName",
}

enum IndexNames {
  INDEX_ROW = "indexRow",
  INDEX_COLUMN = "indexColumn",
  INDEX_LEVEL = "indexLevel",
}

export const WarehouseMapLocationModal = ({
  open,
  closeModal,
  hasError,
  mapSelected,
  mapCellSelected,
  isOnViewMode,
  onCreate,
  onUpdate,
  onClear,
  isLoading,
  createdMapCells,
  levelsSize,
}: Props) => {
  const [levelsIndexesToChange, setLevelsIndexesToChange] = useState<number[]>([]);
  const [selectedLoadedLevels, setSelectedLoadedLevels] = useState<number[]>([]);
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
  const { t: translate } = useTranslation();

  useEffect(() => {
    if (hasError === undefined && !hasError && !isLoading && hasSubmitted) {
      setLevelsIndexesToChange([]);
      setSelectedLoadedLevels([]);
      closeModal();
    }
  }, [hasError, isLoading, hasSubmitted]);

  const mapCells = createdMapCells || mapSelected?.mapCells;

  if ((!mapSelected?.configLevel && !levelsSize) || !mapCells?.length) return <></>;

  const levelsLength = levelsSize || Number(mapSelected?.configLevel?.size);

  const groupBy = useCallback(
    (axisName: AxisNames, indexName: IndexNames, mapCellsSelected: MapCell[]) => {
      const names: string[] = [];

      if (mapCellsSelected?.length) {
        for (const mapCell of mapCellsSelected) {
          if (!names?.includes(mapCell[axisName])) {
            names[mapCell[indexName]] = mapCell[axisName];
          }
        }
      }

      return names;
    },
    [mapSelected]
  );

  function handleSelectLevel(index: number, selected: boolean) {
    let selectedLevels: number[] = [];

    const loadedLevelsIndexes = getLevels(mapCellSelected?.indexColumn, mapCellSelected?.indexRow)
      .filter(
        (level) =>
          (level.storageBoxes && level.storageBoxes.length > 0) ||
          (level.itemsJobs && level.itemsJobs.length > 0)
      )
      .map((level) => level.indexLevel);

    selectedLevels = selected
      ? [
          ...levelsIndexesToChange.filter((selectedLevelsIndex) => selectedLevelsIndex !== index),
          index,
        ]
      : [...levelsIndexesToChange.filter((selectedLevelsIndex) => selectedLevelsIndex !== index)];
    setLevelsIndexesToChange(selectedLevels);

    const loadedIndexes = selectedLevels.filter((selectedLevel) =>
      loadedLevelsIndexes.includes(selectedLevel)
    );

    setSelectedLoadedLevels(loadedIndexes);
  }

  const renderCheckbox = (levelIndex: number) => {
    if (isOnViewMode) return <></>;

    return (
      <Checkbox
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          handleSelectLevel(levelIndex, event.target.checked)
        }
        checked={levelsIndexesToChange.includes(levelIndex)}
      />
    );
  };

  const renderEmptyCheckbox = () => {
    return <BlankCheckbox />;
  };

  const renderInput = (name: string) => {
    return <MapInput value={name} type={"text"} isViewMode={true} disabled={true} />;
  };

  const renderSquareToLevel = (indexColumn?: number, indexRow?: number, indexLevel?: number) => {
    const mapCell = mapCells?.filter(
      (mapCell) =>
        mapCell?.indexLevel === indexLevel &&
        mapCell?.indexRow === indexRow &&
        mapCell?.indexColumn === indexColumn
    )?.[0];

    const mapCellStatus =
      (mapCell?.storageBoxes && mapCell.storageBoxes.length > 0) ||
      (mapCell?.itemsJobs && mapCell?.itemsJobs?.length > 0)
        ? MapStatus.LOADED
        : MapStatus.EMPTY;

    return <MapSquare status={mapCell?.isActive ? mapCellStatus : MapStatus.INACTIVE} />;
  };

  const getLevels = (indexColumn?: number, indexRow?: number) => {
    return mapCells?.filter(
      (mapCell) => mapCell.indexColumn === indexColumn && mapCell.indexRow === indexRow
    );
  };

  const mountLevelGrid = (indexColumn?: number, indexRow?: number, axisName?: string) => {
    if (indexColumn === undefined || indexRow === undefined) return <></>;

    const levelsNames = groupBy(
      AxisNames.LEVEL_NAME,
      IndexNames.INDEX_LEVEL,
      getLevels(indexColumn, indexRow) || []
    );

    const gridLevel = [];

    gridLevel.push([renderEmptyCheckbox(), renderInput(axisName || "")]);

    for (let level = 0; level < levelsLength; level += 1) {
      gridLevel.push([
        renderCheckbox(level),
        renderSquareToLevel(indexColumn, indexRow, level),
        renderInput(levelsNames[level]),
      ]);
    }

    return (
      <MapCellsLevelsWrapper item xs={12}>
        <MapCellsLevelsContainer item xs={12}>
          {gridLevel.reverse().map((level, index) => {
            return (
              <MapRow key={index}>
                {level.map((mapElement: React.ReactNode) => {
                  return mapElement;
                })}
              </MapRow>
            );
          })}
        </MapCellsLevelsContainer>
      </MapCellsLevelsWrapper>
    );
  };

  const handleClearLevel = () => {
    setHasSubmitted(true);
    const levelsToClear = getLevels(
      mapCellSelected?.indexColumn,
      mapCellSelected?.indexRow
    )?.filter((level) => levelsIndexesToChange?.includes(level?.indexLevel));

    levelsToClear?.forEach((level) => {
      if (level?.id) {
        onClear?.(level.id);
      }
    });
  };

  const handleChangeStatusLevel = () => {
    setHasSubmitted(true);
    const levels = getLevels(mapCellSelected?.indexColumn, mapCellSelected?.indexRow);

    if (mapSelected?.id) {
      const levelsToUpdate = levels
        .filter((level) => levelsIndexesToChange?.includes(level?.indexLevel))
        .map((level) => ({ id: level.id, isActive: !level?.isActive } as IUpdateMapCells));

      onUpdate?.(levelsToUpdate);
    } else {
      const mapCellsToUpdate = createdMapCells?.map((mapCell) =>
        mapCell.indexColumn === mapCellSelected?.indexColumn &&
        mapCell.indexRow === mapCellSelected.indexRow &&
        levelsIndexesToChange.includes(mapCell.indexLevel)
          ? { ...mapCell, isActive: !mapCell?.isActive }
          : mapCell
      );

      onCreate?.(mapCellsToUpdate || []);
      setLevelsIndexesToChange([]);
    }
  };

  return (
    <Modal
      open={open}
      handleClose={() => {
        closeModal();
      }}
      title={`${translate("location")} "${mapCellSelected?.name?.split("/")[0]}"`}
      disableEnforceFocus
      data-cy="warehouseMapLocationModal"
      dataCyTitle="warehouseMapLocationTitle"
      showActionButtons
      actionButtonProps={{
        dataCyCancel: "cancelLocationButton",
        onCancel: mapSelected?.id ? () => handleClearLevel() : undefined,
        customColorCancel: Color.TEXT_INFO,
        customColorSave: Color.SECONDARY,
        dataCySave: "submitLocationChangesButton",
        textSave: translate("changeStatus"),
        textCancel: translate("clearLocation"),
        cancelDisabled:
          isLoading || levelsIndexesToChange.length < 1 || selectedLoadedLevels.length < 1,
        saveDisabled:
          isLoading || levelsIndexesToChange.length < 1 || selectedLoadedLevels.length > 0,
        loading: isLoading,
        onSave: () => handleChangeStatusLevel(),
      }}
    >
      <PreviewStatus displayPreviewTitle={false} />
      {mountLevelGrid(
        mapCellSelected?.indexColumn,
        mapCellSelected?.indexRow,
        mapCellSelected?.name?.split("/")[0]
      )}
    </Modal>
  );
};
